import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const BusinessRestrictionsContext = createContext();

export const useBusinessRestrictions = () => {
    const context = useContext(BusinessRestrictionsContext);
    if (!context) {
        throw new Error('useBusinessRestrictions must be used within a BusinessRestrictionsProvider');
    }
    return context;
};

export const BusinessRestrictionsProvider = ({ children }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Assumes useAuth provides the current admin's ID as 'adminId' or within 'user' object
    // and the function to make requests.
    const { makeAuthenticatedRequest, loginType, adminId } = useAuth();

    // API base URL configuration
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';

    // Determine the correct base URL based on login type
    const getRestrictionsBaseUrl = useCallback(() => {
        if (loginType === 'master') {
            return `${API_BASE_URL}/master/business-restrictions/api/v1/business-restrictions`;
        } else if (loginType === 'admin') {
            return `${API_BASE_URL}/admin/business-restrictions/api/v1/business-restrictions`;
        } else {
            throw new Error('Invalid login type for business restrictions access');
        }
    }, [API_BASE_URL, loginType]);

    // Helper function to handle API responses
    const handleApiResponse = async (response) => {
        // Handle 204 No Content (used in Delete)
        if (response.status === 204) {
            return { success: true };
        }

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || data.message || 'Request failed');
        }

        return data;
    };

    // Clear error state
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Validate user access
    const validateAccess = useCallback(() => {
        if (loginType !== 'admin' && loginType !== 'master') {
            throw new Error('Only admin and master users can access business restrictions');
        }
    }, [loginType]);

    // ---------------------------------------------------------------------------
    // CORE OPERATIONS
    // ---------------------------------------------------------------------------

    /**
     * Create a new restriction set for a business.
     * 
     * @param {Object} data - Payload data
     * @param {string} data.profile_id - The UUID of the business profile (Required)
     * @param {string} [data.admin_id] - The UUID of the admin creating this. 
     *                                   If not provided, tries to use current auth ID.
     */
    const createRestriction = useCallback(async (data) => {
        validateAccess();

        if (!data.profile_id) {
            throw new Error('Profile ID is required to create restrictions');
        }

        // Ensure admin_id is present. Use the one passed in args, or fallback to auth context
        const payload = {
            ...data,
            admin_id: data.admin_id || adminId
        };

        if (!payload.admin_id) {
            throw new Error('Admin ID is required. Please ensure you are logged in or provide admin_id.');
        }

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getRestrictionsBaseUrl();
            const response = await makeAuthenticatedRequest(`${baseUrl}/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to initialize restrictions';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getRestrictionsBaseUrl, adminId]);

    // Get restrictions with pagination and filtering
    const getRestrictions = useCallback(async (options = {}) => {
        validateAccess();
        setLoading(true);
        setError(null);

        try {
            const queryParams = new URLSearchParams();

            if (options.limit) queryParams.append('limit', options.limit);
            if (options.offset) queryParams.append('offset', options.offset);
            if (options.profileId) queryParams.append('profile_id', options.profileId);
            if (options.adminId) queryParams.append('admin_id', options.adminId);

            const baseUrl = getRestrictionsBaseUrl();
            const url = `${baseUrl}/`;
            const fullUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;

            const response = await makeAuthenticatedRequest(fullUrl);
            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to fetch restrictions';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getRestrictionsBaseUrl]);

    // In BusinessRestrictionsProvider - update fetchBusinessNameByProfileId
    const fetchBusinessNameByProfileId = useCallback(async (profileId) => {
        if (!profileId) return null;

        try {
            const baseUrl = getRestrictionsBaseUrl(); // Use the same base URL as restrictions
            const response = await makeAuthenticatedRequest(
                `${baseUrl}/business-info/${encodeURIComponent(profileId)}`
            );

            if (!response.ok) {
                console.error('Failed to fetch business name:', response.status);
                return null;
            }

            const data = await response.json();
            return data.business_name || null;
        } catch (error) {
            console.error('Error fetching business name:', error);
            return null;
        }
    }, [makeAuthenticatedRequest, getRestrictionsBaseUrl]);

    // Get a specific restriction set by Restriction ID
    const getRestrictionById = useCallback(async (restrictionId) => {
        validateAccess();

        if (!restrictionId) {
            throw new Error('Restriction ID is required');
        }

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getRestrictionsBaseUrl();
            const response = await makeAuthenticatedRequest(`${baseUrl}/${encodeURIComponent(restrictionId)}`);
            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to get restriction details';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getRestrictionsBaseUrl]);

    // Update specific restriction flags (PUT)
    const updateRestriction = useCallback(async (restrictionId, updates) => {
        validateAccess();

        if (!restrictionId) throw new Error('Restriction ID is required');
        if (!updates || Object.keys(updates).length === 0) throw new Error('No updates provided');

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getRestrictionsBaseUrl();
            const response = await makeAuthenticatedRequest(`${baseUrl}/${encodeURIComponent(restrictionId)}`, {
                method: 'PUT', // Backend supports PUT or PATCH
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updates)
            });
            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to update restrictions';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getRestrictionsBaseUrl]);

    // Delete a restriction set (Re-enables everything by removing the row)
    const deleteRestriction = useCallback(async (restrictionId) => {
        validateAccess();

        if (!restrictionId) throw new Error('Restriction ID is required');

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getRestrictionsBaseUrl();
            const response = await makeAuthenticatedRequest(`${baseUrl}/${encodeURIComponent(restrictionId)}`, {
                method: 'DELETE'
            });
            // 204 No Content handled in handleApiResponse
            await handleApiResponse(response);
            return { success: true, message: 'Restriction removed successfully' };
        } catch (err) {
            const errorMessage = err.message || 'Failed to delete restriction';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getRestrictionsBaseUrl]);

    // ---------------------------------------------------------------------------
    // CONVENIENCE & UTILITY FUNCTIONS
    // ---------------------------------------------------------------------------

    // Get restrictions for a specific business profile
    // This extracts the single restriction object from the array response
    // The result will include 'business_name' and 'business_registry_number'
    const getRestrictionsByProfile = useCallback(async (profileId) => {
        validateAccess();
        if (!profileId) throw new Error('Profile ID is required');

        const result = await getRestrictions({ profileId, limit: 1 });
        return result.restrictions && result.restrictions.length > 0
            ? result.restrictions[0]
            : null;
    }, [getRestrictions, validateAccess]);

    // Toggle a specific flag for a restriction ID
    const toggleSpecificRestriction = useCallback(async (restrictionId, fieldName, currentValue) => {
        return await updateRestriction(restrictionId, {
            [fieldName]: !currentValue
        });
    }, [updateRestriction]);

    // Health check for the service
    const checkHealth = useCallback(async () => {
        validateAccess();

        try {
            const serviceRoot = loginType === 'master'
                ? `${API_BASE_URL}/master/business-restrictions`
                : `${API_BASE_URL}/admin/business-restrictions`;

            const response = await makeAuthenticatedRequest(`${serviceRoot}/health`);
            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Health check failed';
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    }, [makeAuthenticatedRequest, validateAccess, API_BASE_URL, loginType]);

    // Format data for UI display (Counts how many features are restricted)
    const formatRestrictionData = useCallback((restriction) => {
        if (!restriction) return null;

        const flags = [
            restriction.public_appearance_restriction,
            restriction.business_phone_restriction,
            restriction.business_email_restriction,
            restriction.gallery_images_restriction,
            restriction.business_location_restriction,
            restriction.schedule_restriction,
            restriction.services_restriction,
            restriction.team_restriction,
            restriction.socials_restriction,
            restriction.event_ticket_restriction,
            restriction.event_restriction
        ];

        const activeCount = flags.filter(Boolean).length;
        const isFullyUnrestricted = activeCount === 0;
        const isFullyRestricted = activeCount === flags.length;

        let statusLabel = 'Active';
        let statusColor = 'green';

        if (isFullyRestricted) {
            statusLabel = 'Fully Restricted';
            statusColor = 'red';
        } else if (activeCount > 0) {
            statusLabel = `Partially Restricted (${activeCount})`;
            statusColor = 'orange';
        }

        return {
            ...restriction,
            activeRestrictionCount: activeCount,
            isFullyUnrestricted,
            statusLabel,
            statusColor
        };
    }, []);

    // Debugging helper
    const getEndpointInfo = useCallback(() => {
        return {
            loginType,
            baseUrl: loginType === 'master'
                ? `${API_BASE_URL}/master/business-restrictions/api/v1/business-restrictions`
                : `${API_BASE_URL}/admin/business-restrictions/api/v1/business-restrictions`,
            accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
            requiredHeaders: loginType === 'master'
                ? ['Authorization', 'secret_credential']
                : ['Authorization', 'admin_id'],
        };
    }, [loginType, API_BASE_URL]);

    const value = {
        // State
        loading,
        error,

        // Core Actions
        createRestriction,
        getRestrictions,
        getRestrictionById,
        updateRestriction,
        deleteRestriction,
        fetchBusinessNameByProfileId,

        // Convenience
        getRestrictionsByProfile,
        toggleSpecificRestriction,

        // Utilities
        checkHealth,
        formatRestrictionData,
        getEndpointInfo,
        clearError,

        // Computed Accessors
        isAdminUser: loginType === 'admin',
        isMasterUser: loginType === 'master',
    };

    return (
        <BusinessRestrictionsContext.Provider value={value}>
            {children}
        </BusinessRestrictionsContext.Provider>
    );
};